import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import {
  findNodeById,
  generateCustomCss,
  renderValidatedEditorNode,
} from '../../utilities/htmlEditorUtils.js';
import {
  generatePortfolioFontThemeCss,
  withoutFontThemeDefinition,
} from '../../utilities/googleFonts.js';
import AiRecordUpdateReview from '../../../AI/NewBlogSystem/components/AiRecordUpdateReview.jsx';

const SummaryBadge = ({ iconClass, value, label }) => (
  <div className="ai-change-preview-stat">
    <i className={`bi ${iconClass}`} aria-hidden="true"></i>
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

SummaryBadge.propTypes = {
  iconClass: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

const AiChangePreview = ({
  preview,
  onApply,
  onDiscard,
  onRequestChanges,
  onClose,
  viewOnly = false,
}) => {
  const [version, setVersion] = useState('after');
  const [applying, setApplying] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const dialogRef = useRef(null);
  const activeDocument = version === 'before' ? preview.beforeDocument : preview.afterDocument;
  const root = preview.focusedNodeId
    ? findNodeById(activeDocument?.root, preview.focusedNodeId)
    : activeDocument?.root;
  const previewCss = useMemo(() => [
    generateCustomCss(withoutFontThemeDefinition(activeDocument?.classes || {})),
    generatePortfolioFontThemeCss(activeDocument?.classes || {}),
  ].filter(Boolean).join('\n\n'), [activeDocument?.classes]);
  const closePreview = onClose || onDiscard;

  useEffect(() => {
    dialogRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !applying) closePreview?.();
    };
    window.document.addEventListener('keydown', closeOnEscape);
    return () => window.document.removeEventListener('keydown', closeOnEscape);
  }, [applying, closePreview]);

  if (typeof window === 'undefined') return null;

  const applyChanges = async () => {
    if (applying || !preview.summary.hasChanges) return;
    setApplying(true);
    try {
      await onApply?.();
    } finally {
      setApplying(false);
    }
  };

  const summary = preview.summary;
  return createPortal(
    <div className="ai-change-preview-backdrop">
      <section
        className="ai-change-preview-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-change-preview-title"
        tabIndex="-1"
        ref={dialogRef}
      >
        <header className="ai-change-preview-header">
          <div>
            <span className="small text-primary fw-semibold">AI change preview</span>
            <h2 className="h5 mb-0" id="ai-change-preview-title">{preview.title}</h2>
          </div>
          <button
            className="btn-close"
            type="button"
            onClick={closePreview}
            disabled={applying}
            aria-label={viewOnly ? 'Close expanded preview' : 'Keep current version and close'}
          />
        </header>

        <div className="ai-change-preview-body">
          <section className="ai-change-preview-summary">
            <button
              className="ai-change-preview-summary-toggle"
              type="button"
              aria-expanded={summaryExpanded}
              aria-controls="ai-change-preview-summary-content"
              onClick={() => setSummaryExpanded((expanded) => !expanded)}
            >
              <span>
                <i className="bi bi-list-check me-2" aria-hidden="true"></i>
                Changes made
              </span>
              <i
                className={`bi ${summaryExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                aria-hidden="true"
              ></i>
            </button>

            <div
              id="ai-change-preview-summary-content"
              className={`ai-change-preview-summary-content ${summaryExpanded ? 'is-open' : ''}`}
            >
              <div className="ai-change-preview-stats" aria-label="Change summary">
                <SummaryBadge
                  iconClass="bi-fonts"
                  value={summary.textChanges}
                  label="Text"
                />
                <SummaryBadge
                  iconClass="bi-palette"
                  value={summary.classChanges + summary.styleChanges}
                  label="Appearance"
                />
                <SummaryBadge
                  iconClass="bi-sliders"
                  value={summary.attributeChanges}
                  label="Settings"
                />
                <SummaryBadge
                  iconClass="bi-diagram-3"
                  value={
                    summary.schema.added
                    + summary.schema.removed
                    + summary.schema.tagChanges
                    + summary.schema.moves
                  }
                  label="Structure"
                />
                {summary.recordChanges > 0 ? (
                  <SummaryBadge
                    iconClass="bi-database-check"
                    value={summary.recordChanges}
                    label="Saved data"
                  />
                ) : null}
              </div>

              {summary.descriptions.length ? (
                <ul className="ai-change-preview-description small">
                  {summary.descriptions.map((description) => (
                    <li key={description}>{description}</li>
                  ))}
                </ul>
              ) : (
                <div className="alert alert-light border small mb-3">
                  The AI response does not change this content.
                </div>
              )}

              {summary.schema.hasChanges ? (
                <div className="alert alert-warning py-2 small" role="status">
                  <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                  This proposal changes the page structure. Review the proposed version carefully.
                </div>
              ) : null}
              {summary.recordChanges > 0 ? (
                <AiRecordUpdateReview
                  updates={preview.recordUpdates}
                  context={preview.recordContext}
                />
              ) : null}
            </div>
          </section>

          <div className="ai-change-preview-switch" role="group" aria-label="Preview version">
            <button
              className={`btn btn-sm ${version === 'before' ? 'btn-primary' : 'btn-outline-primary'}`}
              type="button"
              onClick={() => setVersion('before')}
              aria-pressed={version === 'before'}
            >
              Current
            </button>
            <button
              className={`btn btn-sm ${version === 'after' ? 'btn-primary' : 'btn-outline-primary'}`}
              type="button"
              onClick={() => setVersion('after')}
              aria-pressed={version === 'after'}
            >
              New version
            </button>
          </div>

          <div
            className="ai-change-preview-viewport"
            onClickCapture={(event) => {
              if (event.target.closest?.('a, button')) event.preventDefault();
            }}
          >
            {previewCss ? <style>{previewCss}</style> : null}
            <div className="html-editor-canvas">
              {root ? renderValidatedEditorNode(root) : (
                <div className="text-muted text-center p-4">This version has no previewable content.</div>
              )}
            </div>
          </div>
        </div>

        {!viewOnly ? <footer className="ai-change-preview-actions">
          <div className="ai-change-preview-action-buttons">
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={onDiscard}
              disabled={applying}
            >
              Keep current version
            </button>
            {onRequestChanges ? (
              <button
                className="btn btn-sm btn-outline-primary"
                type="button"
                onClick={onRequestChanges}
                disabled={applying}
              >
                <i className="bi bi-stars me-2" aria-hidden="true"></i>
                Request changes
              </button>
            ) : null}
            <button
              className="btn btn-sm btn-primary"
              type="button"
              onClick={() => void applyChanges()}
              disabled={applying || !summary.hasChanges}
            >
              {applying ? (
                <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Using version...</>
              ) : (
                <><i className="bi bi-check2 me-2" aria-hidden="true"></i>Use this version</>
              )}
            </button>
          </div>
        </footer> : (
          <footer className="ai-change-preview-actions">
            <button className="btn btn-sm btn-primary" type="button" onClick={closePreview}>
              Back to assistant
            </button>
          </footer>
        )}
      </section>
    </div>,
    window.document.body
  );
};

AiChangePreview.propTypes = {
  preview: PropTypes.shape({
    title: PropTypes.string.isRequired,
    beforeDocument: PropTypes.object.isRequired,
    afterDocument: PropTypes.object.isRequired,
    focusedNodeId: PropTypes.string,
    summary: PropTypes.object.isRequired,
    recordUpdates: PropTypes.object,
    recordContext: PropTypes.object,
  }).isRequired,
  onApply: PropTypes.func,
  onDiscard: PropTypes.func,
  onRequestChanges: PropTypes.func,
  onClose: PropTypes.func,
  viewOnly: PropTypes.bool,
};

export default AiChangePreview;
